import { useAppDispatch, useAppSelector } from "../../../hooks";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { DBSIntegrationSchema } from "../../../utils/validations";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addDBSServiceIntegrationAsync,
  getDBSServiceIntegrationCapabilitiesAsync,
  getDBSServiceIntegrationsAsync,
  handleDBSIntegrationEditor,
  selectDBSIntegrationCapabilities,
  selectDBSIntegrationCapabilitiesLoading,
  selectDBSIntegrations,
  selectDBSIntegrationsActionLoading,
  selectDBSIntegrationsShowEditor,
  selectDatabaseService,
} from "../../../store/database/serviceSlice";
import { DBSCategory, DBSIntegrationCapability } from "../../../types/database";
import {
  getDatabaseServicesAsync,
  selectDatabaseServices,
  selectDatabaseServicesLoading,
} from "../../../store/database/servicesSlice";
import {
  Alert,
  Button,
  Input,
  Modal,
  Select,
  SelectOption,
  Typography,
} from "djuno-design";

const DBSIntegrationCreateModal: React.FC<{ category: DBSCategory }> = ({
  category,
}) => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectDBSIntegrationsShowEditor);

  const integrations = useAppSelector(selectDBSIntegrations);
  const actionLoading = useAppSelector(selectDBSIntegrationsActionLoading);

  const integrationCapabilities = useAppSelector(
    selectDBSIntegrationCapabilities
  );
  const integrationCapabilitiesLoading = useAppSelector(
    selectDBSIntegrationCapabilitiesLoading
  );

  const service = useAppSelector(selectDatabaseService);

  const services = useAppSelector(selectDatabaseServices);
  const servicesLoading = useAppSelector(selectDatabaseServicesLoading);

  const validationParams = DBSIntegrationSchema();

  //form init
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    control,
    watch,
    trigger,
  } = useForm({
    resolver: yupResolver(validationParams),
    mode: "all",
    defaultValues: {
      indexDaysMax: 1,
    },
  });

  const selectedType = watch("type");
  const sourceServiceId = watch("sourceServiceId");
  const destinationServiceId = watch("destinationServiceId");

  //states
  const [selectedIntegration, setSelectedIntegration] =
    useState<DBSIntegrationCapability>();
  const [sourceServiceTypes, setSourceServiceTypes] = useState<Array<string>>(
    []
  );
  const [targetServiceTypes, setTargetServiceTypes] = useState<Array<string>>(
    []
  );

  //recieve all created services
  useEffect(() => {
    if (isOpen) {
      dispatch(getDatabaseServicesAsync({}));
    }
  }, [dispatch, isOpen, category]);

  //get service-integration capabilities
  useEffect(() => {
    if (service && isOpen) {
      dispatch(
        getDBSServiceIntegrationCapabilitiesAsync({
          engine: service.engine,
          clusterId: service.id,
        })
      );
    }
  }, [dispatch, isOpen, service]);

  //create new integration
  const onSubmit = (data: any) => {
    let apiData = {};
    if (service && selectedIntegration) {
      apiData = {
        destinationServiceId: data.destinationServiceId,
        sourceServiceId: data.sourceServiceId,
        type: data.type,
      };

      const parameters: { [key: string]: string } = {};
      if (selectedIntegration.parameters) {
        for (const p of selectedIntegration.parameters) {
          parameters[p.name] = data[p.name];
          // apiData = { ...apiData, [p.name]: data[p.name] };
        }
      }
      if (Object.keys(parameters).length > 0)
        apiData = { ...apiData, parameters };

      // console.log({ apiData });
      dispatch(
        addDBSServiceIntegrationAsync({
          clusterId: service.id,
          engine: service.engine,
          data: apiData,
        })
      ).then((action) => {
        if (action.type === "service/service-integrations/create/fulfilled") {
          handleOnClose();
          dispatch(
            getDBSServiceIntegrationsAsync({
              engine: service.engine,
              clusterId: service.id,
            })
          );
        }
      });
    }
  };

  const integrationOptions: SelectOption<DBSIntegrationCapability>[] =
    useMemo(() => {
      return Array.from(
        integrationCapabilities
          .reduce((map, obj) => map.set(obj.type, obj), new Map())
          .values()
      ).map((ic) => {
        const option: SelectOption<DBSIntegrationCapability> = {
          label: ic.type,
          value: ic.type,
          extraData: ic,
        };
        return option;
      });
    }, [integrationCapabilities]);

  //calculate selected integration and source types and destination types
  useEffect(() => {
    const selectedIntegration = integrationOptions.find(
      (integrationOption) => integrationOption.value === selectedType
    )?.extraData;

    setSelectedIntegration(selectedIntegration);
  }, [selectedType, integrationOptions]);

  useEffect(() => {
    let sources: string[] = [];
    let destinations: string[] = [];

    integrationCapabilities
      .filter((ic) => ic.type === selectedType)
      .forEach((ic) => {
        sources.push(ic.sourceEngine);
        destinations.push(ic.destinationEngine);
      });

    sources = Array.from(new Set(sources).values());
    destinations = Array.from(new Set(destinations).values());

    setSourceServiceTypes(sources);
    setTargetServiceTypes(destinations);
  }, [integrationCapabilities, selectedType]);

  useEffect(() => {
    setValue("indexDaysMax", 1);
  }, [selectedIntegration, setValue]);

  //source services
  const sourceServices = useMemo(() => {
    return services
      .filter((s) => s.status === "READY")
      .filter((s) => sourceServiceTypes.includes(s.engine));
  }, [services, sourceServiceTypes]);

  //create select options from sourceServices
  const sourceServicesOptions: SelectOption[] = useMemo(() => {
    return sourceServices.map((s) => {
      const option: SelectOption = {
        label: s.description,
        value: s.id,
      };
      return option;
    });
  }, [sourceServices]);

  useEffect(() => {
    setValue(
      "sourceServiceId",
      sourceServices.length > 0 ? sourceServices[0].id : ""
    );
    trigger("sourceServiceId");
  }, [selectedType, setValue, sourceServices, trigger]);

  //destination services
  const destinationServices = useMemo(() => {
    return services
      .filter((s) => s.status === "READY")
      .filter((s) => targetServiceTypes.includes(s.engine));
  }, [services, targetServiceTypes]);

  //create select options from destinationServices
  const destinationServicesOptions: SelectOption[] = useMemo(() => {
    return destinationServices.map((s) => {
      const option: SelectOption = {
        label: s.description,
        value: s.id,
      };
      return option;
    });
  }, [destinationServices]);

  useEffect(() => {
    setValue(
      "destinationServiceId",
      destinationServices.length > 0 ? destinationServices[0].id : ""
    );
    trigger("destinationServiceId");
  }, [selectedType, setValue, destinationServices, trigger]);

  const isCreatedBefore = useMemo(() => {
    return !!integrations.find(
      (i) =>
        i.sourceServiceId === sourceServiceId &&
        i.destinationServiceId === destinationServiceId
    );
  }, [destinationServiceId, integrations, sourceServiceId]);

  const handleOnClose = useCallback(() => {
    setSelectedIntegration(undefined);
    setSourceServiceTypes([]);
    setTargetServiceTypes([]);
    reset();
    dispatch(handleDBSIntegrationEditor(false));
  }, [dispatch, reset]);

  useEffect(() => {
    return () => {
      handleOnClose();
    };
  }, [handleOnClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnClose}
      contentClassName="max-w-lg"
      title={`Add an integration`}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mt-5">
          {selectedType &&
            isOpen &&
            (destinationServicesOptions.length === 0 ||
              sourceServicesOptions.length === 0) && (
              <Alert uiType="warning" className="mb-5">
                <div className="flex flex-col">
                  <Typography.Text className="!text-sm">
                    You do not have a service to add this type of integration.
                    Please add a service powered by one of the engines listed
                    below, and a type of public network:
                  </Typography.Text>
                  <div className="flex flex-col mt-2">
                    {sourceServicesOptions.length === 0 &&
                      sourceServiceTypes.map((type, i) => (
                        <Typography.Text
                          key={i}
                          className="!text-sm !font-medium"
                        >
                          - {type}
                        </Typography.Text>
                      ))}
                    {destinationServicesOptions.length === 0 &&
                      targetServiceTypes.map((type, i) => (
                        <Typography.Text
                          key={i}
                          className="!text-sm !font-medium"
                        >
                          - {type}
                        </Typography.Text>
                      ))}
                  </div>
                </div>
              </Alert>
            )}
          {isCreatedBefore && isOpen && (
            <Alert uiType="warning" className="mb-5">
              <Typography.Text className="!text-sm">
                A similar integration already exists.
              </Typography.Text>
            </Alert>
          )}

          <Controller
            control={control}
            name="type"
            render={({ field: { value, onChange } }) => (
              <Select<DBSIntegrationCapability>
                value={value?.toString()}
                onChange={(v) => {
                  // setValue("sourceServiceId", "");
                  // setValue("destinationServiceId", "");
                  onChange(v);
                }}
                options={[...integrationOptions]}
                error={errors.type?.message}
                label="Integration type"
                required
                emptyString="Select an integration type"
                loading={integrationCapabilitiesLoading}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-2 items-start w-full gap-5 mt-5">
          <Controller
            control={control}
            name="sourceServiceId"
            render={({ field: { value, onChange } }) => (
              <Select
                disabled={sourceServicesOptions.length <= 1}
                value={value?.toString() || undefined}
                onChange={onChange}
                options={sourceServicesOptions}
                error={errors.sourceServiceId?.message}
                label="Source service"
                required
                emptyString=""
                className="col-span-1"
                loading={servicesLoading}
              />
            )}
          />
          <Controller
            control={control}
            name="destinationServiceId"
            render={({ field: { value, onChange } }) => (
              <Select
                disabled={destinationServicesOptions.length <= 1}
                value={value?.toString() || undefined}
                onChange={onChange}
                options={destinationServicesOptions}
                error={errors.destinationServiceId?.message}
                label="Target service"
                required
                emptyString=""
                className="col-span-1"
                loading={servicesLoading}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-2 w-full gap-5 mt-5">
          {selectedIntegration?.parameters?.map((parameter, i) => {
            return (
              <div className="col-span-1" key={i}>
                <Input
                  label={parameter.name}
                  {...register(
                    parameter.name as "topic" | "indexPrefix" | "indexDaysMax"
                  )}
                  type={
                    parameter.type === "integer" ? "number" : parameter.type
                  }
                  error={
                    errors[
                      parameter.name as "topic" | "indexPrefix" | "indexDaysMax"
                    ]?.message
                  }
                  className=""
                />
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button uiType="light" onClick={handleOnClose}>
            Cancel
          </Button>

          <Button
            uiType="primary"
            type="submit"
            disabled={isCreatedBefore}
            loading={actionLoading}
          >
            Confirm
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default DBSIntegrationCreateModal;
